import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getComponentList,
  addComponentToProduct,
  updateComponent,
  deleteComponent,
  getAllComponentList,
  addAllComponentToProduct,
  updateAllComponent,
  deleteAllComponent,
} from "../../../../../../apis/Product/ComponentApi";
// import { getProdComponentDropdown } from "../../../../../../apis/Dropdown";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import { useBonsaiData } from "../../../../../../hooks/useBonsaiData";

const ProdComponentTable = ({ productId, subcategoryId, isSubcategory }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});

  // Use cached Bonsai data instead of individual API call
  const { data: dropdownUsedFor, isLoading: isBonsaiLoading } = useBonsaiData();

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    if (productId && subcategoryId && isSubcategory) {
      getComponentList({
        productId,
        subcategoryId,
        setObjects: setData,
      });
    } else if (!isSubcategory) {
      getAllComponentList({
        productId,
        setObjects: setData,
      });
    }
  }, [productId, subcategoryId, isSubcategory]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (data.length > 0) {
      const newUnitMaterials = {};
      data.forEach((row) => {
        const materialValue = row["input"]?.value || row.input;
        if (materialValue) {
          const selectedMaterial = dropdownUsedFor.find(
            (option) => option?.value === materialValue
          );
          if (selectedMaterial) {
            newUnitMaterials[row.id] = selectedMaterial.unit;
          }
        }
      });
      setUnitMaterials(newUnitMaterials);
    }
  }, [data, dropdownUsedFor]);

  const dynamicPlaceholders = useMemo(
    () => ({
      input: {
        targetField: "qty",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          rowData,
          rowId = "new"
        ) => {
          const selectedMaterial = dropdownData?.find(
            (option) => option?.value === selectedValue
          );
          if (selectedMaterial) {
            // For existing rows
            if (rowId !== "new") {
              setUnitMaterials((prev) => ({
                ...prev,
                [rowId]: selectedMaterial.unit,
              }));
            } else {
              setUnitMaterials((prev) => ({
                ...prev,
                new: selectedMaterial.unit, // Use a special key for new rows
              }));
            }
          }
        },
        dropdownDataKey: "input",
      },
    }),
    []
  );
  const handleEdit = useCallback(
    (editedItem) => {
      const foundProduct = dropdownUsedFor?.find(
        (option) => option.name === editedItem.input.value
      );
      const product_code = foundProduct
        ? foundProduct["code"]
        : editedItem.product_code;
      if (isSubcategory) {
        updateComponent({
          productId,
          subcategoryId: subcategoryId,
          componentId: editedItem.id,
          ...editedItem,
          input: editedItem.input.value,
          objects: data,
          setObjects: setData,
          use_swal: true,
          product_code: product_code,
        });
      } else {
        updateAllComponent({
          productId,
          componentId: editedItem.id,
          ...editedItem,
          input: editedItem.input.value,
          objects: data,
          setObjects: setData,
          use_swal: true,
          product_code: product_code,
        });
      }
    },
    [productId, data, setData, subcategoryId, isSubcategory, dropdownUsedFor]
  );

  const handleAdd = useCallback(
    (newItem) => {
      const foundProduct = dropdownUsedFor?.find(
        (option) => option.name === newItem.input.value
      );

      const product_code = foundProduct ? foundProduct["code"] : "";
      if (isSubcategory) {
        addComponentToProduct({
          productId,
          subcategoryId: subcategoryId,
          ...newItem,
          input: newItem.input.value,
          callback: fetchData,
          use_swal: true,
          product_code: product_code,
        });
      } else {
        addAllComponentToProduct({
          productId,
          ...newItem,
          input: newItem.input.value,
          callback: fetchData,
          use_swal: true,
          product_code: product_code,
        });
      }
    },
    [productId, fetchData, subcategoryId, isSubcategory, dropdownUsedFor]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      if (isSubcategory) {
        deleteComponent({
          productId,
          subcategoryId: subcategoryId,
          componentId: itemToDelete.id,
          callback: fetchData,
        });
      } else {
        deleteAllComponent({
          productId,
          componentId: itemToDelete.id,
          callback: fetchData,
        });
      }
    },
    [productId, fetchData, subcategoryId, isSubcategory]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "input",
        header: `${t("COMPONENT")}*`,
        type: "dropdown",
        dropdownData: dropdownUsedFor,
        disableSortBy: true,
      },
      {
        accessor: "qty",
        header: t("QTY_FUNC_UNIT"),
        type: "number",
        tipHeader: "Tip",
        tipBody: t("TIP_QTY_FUNC_COMP"),
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
        unit: (rowId, rowData) => {
          const rowType = rowData?.input?.value || rowData?.["input"]?.value;
          return rowType
            ? dropdownUsedFor.find((option) => option.value === rowType)?.unit
            : unitMaterials[rowId] || "xx";
        },
      },
      {
        accessor: "manuel",
        header: (props) => {
          const rowId = props.row?.original?.id;
          return `${t("Manual kgCO₂")} /${unitMaterials[rowId] || "xx"}`;
        },
        type: "number",
        tipHeader: "Tip",
        tipBody: t("TIP_MANUEL_PROD"),
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
        unit: (rowId, rowData) => {
          const rowType = rowData?.type?.value || rowData?.["input"]?.value;
          return rowType
            ? "kgCO₂/" +
                dropdownUsedFor.find((option) => option.value === rowType)?.unit
            : unitMaterials[rowId]
            ? "kgCO₂/" + unitMaterials[rowId]
            : "kgCO₂/xx";
        },
        optional: true,
      },
      {
        accessor: "supplier",
        header: t("SUPPLIER"),
        type: "text",
        disableSortBy: true,
        placeholder: t("TYPE_HERE"),
        width: "120px",
      },
      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "40px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "120px",
      },
    ],
    [dropdownUsedFor, t, unitMaterials]
  );

  const disableRules = {
    carbon: () => true,
  };

  const subtotalColumns = {
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      input: dropdownUsedFor,
    }),
    [dropdownUsedFor]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default ProdComponentTable;
